import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth, useSpace } from '@/auth/useAuth'
import { SPACE_ROLE_LABELS } from '@/auth/permissions'
import { SpaceMembers } from './SpaceMembers'
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  KeyValue,
  Skeleton,
} from '@/components/ui'
import './pages.css'

interface SpaceRow {
  id: string
  slug: string
  name: string
  description: string | null
  kind: 'bereich' | 'projekt'
  visibility: 'private' | 'internal'
}

/**
 * Bereichsübersicht.
 *
 * Es wird bewusst kein Filter auf Sichtbarkeit im Client gebaut: die Abfrage
 * liefert ohnehin nur, was die RLS-Policy durchlaesst. Ein zusaetzlicher
 * Client-Filter wuerde nur vortaeuschen, dass er etwas absichert.
 */
export function SpacesIndex() {
  const [spaces, setSpaces] = useState<SpaceRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true

    supabase
      .from('spaces')
      .select('id, slug, name, description, kind, visibility')
      .is('archived_at', null)
      .order('name')
      .then(({ data, error: queryError }) => {
        if (!active) return
        if (queryError) setError(queryError.message)
        else setSpaces((data ?? []) as SpaceRow[])
      })

    return () => {
      active = false
    }
  }, [])

  return (
    <div className="stack-lg">
      <header className="stack-sm">
        <p className="eyebrow">Organisation</p>
        <h1>Bereiche</h1>
        <p className="text-secondary prose">
          Engineering-Bereiche und Projekte. Was Sie hier sehen, hängt an Ihrer
          Mitgliedschaft — die Datenbank liefert nur freigegebene Einträge aus.
        </p>
      </header>

      {error && <Alert tone="danger" title="Abfrage fehlgeschlagen">{error}</Alert>}

      {spaces === null && !error && (
        <div className="grid-auto">
          {[0, 1, 2].map((i) => (
            <div className="surface" key={i} style={{ padding: 'var(--space-5)' }}>
              <Skeleton width="45%" height="1.1em" />
              <div style={{ height: 'var(--space-3)' }} />
              <Skeleton width="90%" />
            </div>
          ))}
        </div>
      )}

      {spaces?.length === 0 && (
        <EmptyState
          title="Keine Bereiche sichtbar"
          description="Entweder gibt es noch keine, oder Sie sind keinem zugeordnet."
        />
      )}

      {spaces && spaces.length > 0 && (
        <div className="grid-auto">
          {spaces.map((space) => (
            <Link key={space.id} to={`/spaces/${space.slug}`} className="surface space-card">
              <div className="cluster-sm">
                <Badge tone={space.kind === 'projekt' ? 'info' : 'neutral'}>
                  {space.kind === 'projekt' ? 'Projekt' : 'Bereich'}
                </Badge>
                {space.visibility === 'internal' && (
                  <Badge tone="neutral" outline>
                    Intern sichtbar
                  </Badge>
                )}
              </div>
              <h2 className="space-card__name">{space.name}</h2>
              {space.description && (
                <p className="space-card__desc text-tertiary">{space.description}</p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

/** Einzelner Bereich. */
export function SpaceDetail() {
  const { slug } = useParams<{ slug: string }>()
  const membership = useSpace(slug)
  const { permissions } = useAuth()

  const [space, setSpace] = useState<SpaceRow | null | undefined>(undefined)

  useEffect(() => {
    if (!slug) return
    let active = true

    supabase
      .from('spaces')
      .select('id, slug, name, description, kind, visibility')
      .eq('slug', slug)
      .maybeSingle()
      .then(({ data }) => {
        if (active) setSpace((data as SpaceRow) ?? null)
      })

    return () => {
      active = false
    }
  }, [slug])

  if (space === undefined) {
    return <Skeleton width="60%" height="2em" />
  }

  if (space === null) {
    return (
      <EmptyState
        title="Bereich nicht gefunden"
        description="Er existiert nicht, ist archiviert, oder Sie haben keinen Zugriff darauf. Die Datenbank unterscheidet das absichtlich nicht."
        action={<Button onClick={() => history.back()}>Zurück</Button>}
      />
    )
  }

  const effective = membership
    ? membership.permissions
    : [...permissions].filter((p) => p.startsWith('space:'))

  return (
    <div className="stack-lg">
      <header className="stack-sm">
        <p className="eyebrow">{space.kind === 'projekt' ? 'Projekt' : 'Bereich'}</p>
        <h1>{space.name}</h1>
        {space.description && <p className="text-secondary prose">{space.description}</p>}
      </header>

      <div className="grid-auto">
        <Card title="Ihre Rolle hier">
          <KeyValue
            items={[
              {
                label: 'Rolle',
                value: membership ? (
                  <Badge tone="accent">{SPACE_ROLE_LABELS[membership.role]}</Badge>
                ) : (
                  <span className="text-tertiary">Kein Mitglied</span>
                ),
              },
              { label: 'Kennung', value: space.slug, mono: true },
              {
                label: 'Sichtbarkeit',
                value: space.visibility === 'internal' ? 'Intern' : 'Nur Mitglieder',
              },
            ]}
          />
        </Card>

        <Card title="Wirksame Rechte">
          {effective.length === 0 ? (
            <p className="text-tertiary">Keine Rechte in diesem Bereich.</p>
          ) : (
            <ul className="perm-chips">
              {effective.map((permission) => (
                <li key={permission}>
                  <code className="perm-chip">{permission}</code>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <SpaceMembers spaceId={space.id} />
    </div>
  )
}
